import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Add this import
import { useAuth } from '../../../AuthContext';
import { db } from '../../../firebaseConfig';
import {
    doc,
    getDoc,
    collection,
    query,
    where,
    getDocs,
    Timestamp,
    limit,
    updateDoc,
    writeBatch
} from 'firebase/firestore';
import GradientSubmitButton from '../../GradientSubmitButton';
import Toast from '../../Toast';
import { LeadData } from '../../../pages/Clients/Admin/Function/LeadFunctions';
import getCurrentScreen from '../../../Helper/GetScreen';
import { useAppSelector } from '../../../redux/store';

// Types
interface User {
    id: string;
    name: string;
}

interface TransferModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedStudent: LeadData | null;
    selectedStudentPhone: string;
    currentAssignedUser: User | null;
    onSuccess?: (message: string) => void;
}

const TransferModal: React.FC<TransferModalProps> = ({
    isOpen,
    onClose,
    selectedStudent,
    selectedStudentPhone,
    currentAssignedUser,
    onSuccess
}) => {
    const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
    const navigate = useNavigate(); // Add this hook
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    // State variables
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<User[]>([]);
    const [selectedTransferUser, setSelectedTransferUser] = useState<User | null>(null);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isError, setIsError] = useState(false);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setSearchResults([]);
            setSelectedTransferUser(null);
            setSubmitLoading(false);
        }
    }, [isOpen]);

    // Fetch available users when modal opens
    useEffect(() => {
        if (isOpen && userData?.companyId) {
            fetchAvailableUsers();
        }
    }, [isOpen, userData?.companyId]);

    const handleSearchUsers = async (value: string) => {
        if (!userData?.companyId || value.length < 3) {
            setSearchResults([]);
            return;
        }

        setIsLoadingUsers(true);
        try {
            const upperValue = value.toUpperCase();
            const usersRef = collection(db, 'users');
            const q = query(
                usersRef,
                where('companyId', '==', userData.companyId),
                where('name', '>=', upperValue),
                where('name', '<=', upperValue + '\uf8ff'),
                limit(10)
            );

            const querySnapshot = await getDocs(q);
            const usersData = querySnapshot.docs
                .filter(doc => doc.data().isActive !== false) // Filter out deactivated users
                .map(doc => ({
                    id: doc.id,
                    name: doc.data().name
                }));

            setSearchResults(usersData);
        } catch (error) {
            console.error('Error searching users:', error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    // Fetch available users with same companyId
    const fetchAvailableUsers = async () => {
        if (!userData?.companyId) return;

        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('companyId', '==', userData.companyId));
            const querySnapshot = await getDocs(q);

            const usersData = querySnapshot.docs
                .filter(doc => doc.data().isActive !== false) // Filter out deactivated users
                .map(doc => ({
                    id: doc.id,
                    name: doc.data().name
                }));

            setSearchResults(usersData);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleSelectTransferUser = (user: User) => {
        setSelectedTransferUser(user);
    };

    const handleTransferSubmit = async () => {
        if (!selectedStudent || !userData?.companyId || !selectedTransferUser) return;

        setSubmitLoading(true);
        try {
            // Find the lead document of this specific student
            const leadRef = doc(db, `companies/${userData.companyId}/leads`, selectedStudent.id);

            // get the activeFollowUpId of this specific student
            const leadDoc = await getDoc(leadRef);
            const leadData = leadDoc?.data();
            const activeFollowUpId = leadData?.activeFollowUpId;

            // if activeFollowUpId is present, find the followUp document of this specific student
            const followUpRef = activeFollowUpId ? doc(db, `companies/${userData.companyId}/followUps`, activeFollowUpId) : null;

            const batch = writeBatch(db);

            const now = new Date();
            const transferTime = new Date(now.getTime() + 1000); // Add 1 second to ensure transferredAt > updatedAt

            // Update assignedTo field of this specific student
            batch.update(leadRef, {
                assignedTo: selectedTransferUser.id,
                transferredAt: Timestamp.fromDate(transferTime),
                updatedBy: currentUser?.uid,
                updatedAt: Timestamp.fromDate(now),
            });

            // If there's an active follow-up, update its userId as well
            if (followUpRef) {
                batch.update(followUpRef, {
                    userId: selectedTransferUser.id,
                    updatedAt: Timestamp.fromDate(new Date()),
                });
            }

            // Commit the batch
            await batch.commit();

            const successMessage = `Lead successfully transferred to ${selectedTransferUser.name}!`;

            // Reset state and close modal immediately
            setShowToast(false);
            onClose();

            if (onSuccess) {
                onSuccess(successMessage);
            }

            // Navigate to LeadManagement page after successful transfer
            setTimeout(() => {
                navigate('/leadmanagement'); // Adjust the path according to your routing setup
                window.location.reload();
            }, 100); // Small delay to ensure modal closes smoothly

        } catch (error) {
            const errorMessage = 'Failed to transfer lead. Please try again.';
            setToastMessage(errorMessage);
            setShowToast(true);
            setIsError(true);
            console.error('Error transferring lead:', error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleCancel = () => {
        setSearchTerm('');
        setSearchResults([]);
        setSelectedTransferUser(null);
        onClose();
    };

    if (!isOpen || !selectedStudent) {
        return null;
    }

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className={`bg-white rounded-3xl w-[500px] max-h-[90vh] overflow-y-auto ${submitLoading ? 'cursor-not-allowed' : ''}`}>
                    <div className="p-6">
                        {submitLoading && (
                            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-3xl">
                                <div className="flex flex-col items-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mb-2"></div>
                                    <p className="text-sm text-gray-600">Transferring lead...</p>
                                </div>
                            </div>
                        )}
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-medium">Transfer Lead</h3>
                            <button
                                onClick={handleCancel}
                                className={`text-gray-500 hover:text-gray-700 ${submitLoading ? 'cursor-not-allowed opacity-50' : ''}`}
                                disabled={submitLoading}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="mb-6">
                            <p className="text-sm text-gray-600 mb-1">Student Name</p>
                            <p className="font-medium">{selectedStudent.name}</p>
                            <p className="text-sm text-gray-600 mt-3 mb-1">Phone Number</p>
                            <p className="font-medium">{selectedStudentPhone}</p>
                            {isAdmin && currentScreen === "Admin" && (
                                <>
                                    <p className="text-sm text-gray-600 mt-3 mb-1">Current Relationship Manager</p>
                                    <p className="font-medium">{currentAssignedUser?.name || 'Not assigned'}</p>
                                </>
                            )}
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm text-gray-600 mb-2">Search Users</label>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSearchTerm(value);
                                    handleSearchUsers(value);
                                }}
                                placeholder="Type to search users..."
                                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 ${submitLoading ? 'cursor-not-allowed opacity-50' : ''}`}
                                disabled={submitLoading}
                            />
                        </div>

                        {isLoadingUsers ? (
                            <div className="text-center py-4">Loading...</div>
                        ) : (
                            <div className="max-h-48 overflow-y-auto mb-6">
                                {searchResults.map(user => (
                                    <div
                                        key={user.id}
                                        onClick={() => !submitLoading && handleSelectTransferUser(user)}
                                        className={`p-3 rounded-lg mb-2 ${submitLoading
                                                ? 'cursor-not-allowed opacity-50'
                                                : 'cursor-pointer hover:bg-gray-100'
                                            } ${selectedTransferUser?.id === user.id
                                                ? 'bg-cyan-100 border-2 border-cyan-500'
                                                : ''
                                            }`}
                                    >
                                        {user.name}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={handleCancel}
                                className={`px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 ${submitLoading ? 'cursor-not-allowed opacity-50' : ''}`}
                                disabled={submitLoading}
                            >
                                Cancel
                            </button>

                            <GradientSubmitButton
                                onClick={handleTransferSubmit}
                                label="Transfer"
                                loadingLabel="Transferring..."
                                loading={submitLoading}
                                disabled={!selectedTransferUser || submitLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={isError}
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </>
    );
};

export default TransferModal;